






































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref } from 'vue-property-decorator';

/**
 *  Alignment of the tooltip.
 */
enum TooltipAlign {
    /** Aligning the tooltip to the left, having the pointer right. */
    Left = 'left',

    /** Aligning the tooltip in the center, having the pointer in the middle. */
    Center = 'center',

    /** Aligning the tooltip to the right, having the pointer left. */
    Right = 'right',
}

/**
 *  Component for rendering text with an interactive interrogation
 *  icon and tooltip with additional information.
 */
@Component
export default class Interrogation extends Vue {

    /**
     *  Holding a reference to the tooltip element.
     */
    @Ref('tooltip')
    private tooltip!: HTMLElement;

    @Prop({ type: String, default: '15rem' })
    private tooltipWidth!: string;

    @Prop({ type: Boolean })
    private boldLabel!: boolean;

    /**
     *  The updating state, used to prevent race conditions.
     */
    private updating = false;

    /**
     *  The show state of the tooltip.
     */
    private showTooltip = false;

    /**
     *  The current alignment of the tooltip.
     */
    private tooltipAlign = TooltipAlign.Center;

    /**
     *  Holding the value of the small breakpoint, set
     *  in the `mounted()` hook and used to compute if
     *  the component is used on a smaller screen.
     */
    private smallBreakpoint = '0px';

    /**
     *  Get the value of the small breakpoint when mounted,
     *  so it can be used in the `isSmallScreen()` method.
     */
    private mounted(): void {

        this.smallBreakpoint = getComputedStyle(document.body).getPropertyValue('--ccos-breakpoint-md');

    }

    /**
     *
     *  Used to toggle the information tooltip.
     *
     *  (i) Listens to mouse enters and leaves on larger
     *  screens and to clicks on smaller screens to prevent
     *  weird behaviour, caused by the hover behaviours
     *  on touch devices.
     *
     *  @param event The pointer event of the user's cursor
     *
     */
    private toggleTooltip(event: PointerEvent): void {

        if (this.updating) {

            return;

        }

        this.updating = true;

        switch (event.type) {

            case 'mouseenter':
            case 'mouseleave':

                if (!this.isSmallScreen()) {

                    this.showTooltip = event.type === 'mouseenter' ? true : false;

                    this.$nextTick(this.checkDirection.bind(this));

                }

                break;

            case 'click':

                if (this.isSmallScreen()) {

                    this.showTooltip = !this.showTooltip;

                }

                break;

            default:

                throw new Error(`toggleTooltip → Unknown event type: "${ event.type }"`);

        }

        this.updating = false;

    }

    /**
     *
     *  Used to check if the interrogation is used on a small screen.
     *
     *  @return If the interrogation is used on a small screen
     *
     */
    private isSmallScreen(): boolean {

        return window.matchMedia(`(max-width: ${ this.smallBreakpoint })`).matches;

    }

    /**
     *  Used to update the alignment of the tooltip to
     *  ensure it stays within the viewport.
     */
    private checkDirection(): void {

        const margin = 50; // Number of pixels the tooltip should be away from the edge
        const rect: DOMRect = this.tooltip.getBoundingClientRect();

        if (rect.left <= margin && this.tooltipAlign !== TooltipAlign.Right) {

            this.tooltipAlign = TooltipAlign.Right;

        } else if (rect.right >= (window.innerWidth - margin) && this.tooltipAlign !== TooltipAlign.Left) {

            this.tooltipAlign = TooltipAlign.Left;

        } else {

            this.tooltipAlign = TooltipAlign.Center;

        }

    }

}
